import { Feather } from '@expo/vector-icons';
import { TouchableOpacity, View } from 'react-native';
import { observer } from 'mobx-react';
import { router } from 'expo-router';

import { ActivityInfo, ActivityType } from '../../ActivityInfo/ActivityInfo';
import { Body1Bold, Caption1Bold } from '../../../config/designSystem/typography';
import { COLORS } from '../../../config/designSystem/Colors';
import { ProgramCardWrapper, line } from '../../Program/ProgramCardWrapper';
import { SPACING } from '../../../config/designSystem/Spacing';
import { activityKey, branchingKey, contentStackKey, dashboardStackKey, privateKey, sessionStackKey } from '../../../constants/routeKeys';
import { concatPath } from '../../../utils/links/concatPath';
import { useStores } from '../../../hooks/useStores';

export type ActivityCardProps = {
  activityId: string;
  line: line;
  /** gap to the next card, which the timeline line runs through */
  marginBelow?: number;
}

const activityTypeToLabel = (type?: string): ActivityType => {
  switch (type) {
  case 'audio':
    return ActivityType.MEDITATION;
  case 'memo':
    return ActivityType.TOOLING;
  case 'journaling':
  default:
    return ActivityType.JOURNALING;
  }
};

/**
 * figma: the Today list challenge card — title, type, days progress (n/7)
 * and a play button for audio challenges.
 */
const _ActivityCard = ({ activityId, line, marginBelow }: ActivityCardProps) => {
  const rootStore = useStores();
  const activity = rootStore.activity.getActivity(activityId);
  const doneToday = rootStore.activity.isDoneToday(activityId);

  const onPressActivity = () => {
    router.navigate({
      pathname: concatPath([privateKey, dashboardStackKey, contentStackKey, activityKey], -1) as string,
      params: { id: activityId }
    });
  };

  const onPressPlay = () => {
    if (!activity?.sessionId || doneToday) {
      onPressActivity();
      return;
    }
    // a fresh run, never the rewrite of an entry left pending by an activity
    // page the user walked away from
    rootStore.activity.setEntryToEdit(null);
    // each day starts blank
    rootStore?.responses?.clearSessionResponses(activity.sessionId);
    router.push({
      pathname: concatPath([privateKey, sessionStackKey, branchingKey], -1) as string,
      params: {
        sessionId: activity.sessionId,
        source: 'SESSION',
      },
    });
  };

  if (!activity) {
    return null;
  }

  const days = activity.days?.slice() || [];
  const doneCount = days.filter((day: { done: boolean }) => day.done).length;
  const showProgress = activity.isChallenge && days.length > 0;
  const showPlay = activity.type === 'audio' && !doneToday;

  return (
    <ProgramCardWrapper done={doneToday} onPress={onPressActivity} line={line} marginBelow={marginBelow}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
        <View style={{ flex: 1, rowGap: SPACING['sp-8'] }}>
          <Body1Bold>{activity.title}</Body1Bold>
          <ActivityInfo type={activityTypeToLabel(activity.type)} />
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', columnGap: SPACING['sp-12'] }}>
          {showProgress && (
            <Caption1Bold color={COLORS.content.secondary}>
              {`${doneCount}/${days.length}`}
            </Caption1Bold>
          )}
          {showPlay && (
            <TouchableOpacity
              onPress={onPressPlay}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              style={{
                width: 40,
                height: 40,
                borderRadius: 20,
                backgroundColor: COLORS.content.accent,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Feather name="play" size={18} color={COLORS.content['inverse-primary']} style={{ marginLeft: 2 }} />
            </TouchableOpacity>
          )}
        </View>
      </View>
    </ProgramCardWrapper>
  );
};

export const ActivityCard = observer(_ActivityCard);
